import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { forkJoin } from 'rxjs';
import { BankAccountService } from '../bank-account/bank-account.service';
import { BankAccount } from '../bank-account/bank-account.model';
import { ChartOfAccounts } from '../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../chart-of-accounts/chart-of-accounts.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { WorkforceCashAdvance, WorkforceCashSettlement, WorkforcePerson, WorkforcePayoutStatement } from './workforce-cash.models';
import { WorkforceCashService } from './workforce-cash.service';
import { ApOverlap, DeliveryCashRequest, LiabilityOption, PettyCashBox, PettyCashCount, PettyCashCustodian, PettyCashCustodyTransfer, PettyCashDocument, PettyCashLine, PettyCashService, WorkerPayAdvance } from './petty-cash.service';

type PettyDialogMode = 'BOX' | 'ACTIVATE' | 'HANDOFF' | 'APPROVE' | 'REJECT' | 'FUND_APPROVE' | 'TRANSFER' | 'CANCEL_TRANSFER'
  | 'WAGE_RETURN' | 'COUNT' | 'CLOSE' | 'CANCEL_OFFSET';
export type PettyCashWorkspaceCommand = 'BOX' | 'ACTIVATE' | 'TRANSFER' | 'CLOSE' | 'REFRESH';

@Component({
  selector: 'app-petty-cash-workspace', standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule,
    LazyLocationPickerDirective],
  templateUrl: './petty-cash-workspace.component.html',
  styleUrls: ['./petty-cash-workspace.component.css']
})
export class PettyCashWorkspaceComponent implements OnChanges {
  @Input() companyId?: number;
  @Input() people: WorkforcePerson[] = [];
  @Input() payouts: WorkforcePayoutStatement[] = [];
  @Input() command?: { name: PettyCashWorkspaceCommand; id: number };
  @Output() boxChanged = new EventEmitter<PettyCashBox | undefined>();
  @Output() commandHandled = new EventEmitter<void>();
  private boxesLoading = false;
  boxes: PettyCashBox[] = []; book: PettyCashDocument[] = []; pending: PettyCashDocument[] = [];
  custodians: PettyCashCustodian[] = [];
  activeTransfer?: PettyCashCustodyTransfer;
  accounts: ChartOfAccounts[] = []; banks: BankAccount[] = []; counts: PettyCashCount[] = [];
  payAdvances: WorkerPayAdvance[] = []; liabilityOptions: LiabilityOption[] = [];
  billOptions: LiabilityOption[] = []; selectedBill?: LiabilityOption; billSearch = '';
  selectedReturnSource = '';
  workerDocuments: PettyCashDocument[] = []; apOverlaps: ApOverlap[] = [];
  deliveryCashRequests: DeliveryCashRequest[] = [];
  awaitingPayroll: PettyCashDocument[] = [];
  operationalAdvances: WorkforceCashAdvance[] = []; approvedSettlements: WorkforceCashSettlement[] = [];
  dueSettlements: WorkforceCashSettlement[] = [];
  selectedBoxId?: number; selectedLiability?: LiabilityOption;
  error = ''; accountsLoadError = ''; success = ''; busy = false; dialogVisible = false;
  dialogMode?: PettyDialogMode; dialogGroup?: PettyCashDocument[]; dialogDocument?: PettyCashDocument;
  actionReason = '';
  boxForm = { boxName: '', custodianId: undefined as number | undefined, locationId: undefined as number | undefined, currencyCode: 'SAR', cashAccountId: undefined as number | undefined,
    workerFloatAccountId: undefined as number | undefined, workerPayAdvanceAccountId: undefined as number | undefined };
  fundingBankId?: number; fundingReference = ''; transferCustodianId?: number; transferReason = '';
  activationCount?: number; countAmount?: number; countWitness = ''; closeMonthValue = '';
  line: PettyCashLine = { purpose: 'DIRECT_EXPENSE', amount: 0 };
  lines: PettyCashLine[] = [];
  submissionKey = ''; offsetKey = ''; private offsetFingerprint = ''; private submissionFingerprint = '';
  approvalWitness = ''; approvalProof = ''; financeProofFile?: File;
  returnReason = ''; returnWitness = ''; returnProof = '';
  readonly purposes = [
    { value: 'DIRECT_EXPENSE', label: 'Buy directly from cash box' },
    { value: 'BILL_PAYMENT', label: 'Pay an existing posted bill' },
    { value: 'FLOAT_ISSUE', label: 'Give company expense float to worker' },
    { value: 'FLOAT_TOP_UP', label: 'Top up worker company expense float' },
    { value: 'FLOAT_RETURN', label: 'Return unused worker float' },
    { value: 'REIMBURSE_EXPENSE', label: 'Reimburse approved out-of-pocket expense' },
    { value: 'PAY_ADVANCE', label: 'Advance against worker pay' },
    { value: 'PAY_ADVANCE_REPAY', label: 'Repay advance against pay' },
    { value: 'EARNED_PAY', label: 'Pay posted commission or earnings' },
    { value: 'PAY_ADVANCE_OFFSET', label: 'Apply advance against posted pay' },
    { value: 'FUND', label: 'Replenish from company bank' }
  ];
  constructor(private petty: PettyCashService, private workforce: WorkforceCashService, private chart: ChartOfAccountsService,
    private bankService: BankAccountService, private session: SessionStorageService, private permissions: PermissionService) {}
  get canManage(): boolean { return this.permissions.hasPermission('accounts.petty-cash.boxes.manage'); }
  get canApprove(): boolean { return this.permissions.hasPermission('accounts.workforce-finance.recoveries.confirm'); }
  get selectedBox(): PettyCashBox | undefined { return this.boxes.find(box => box.pettyCashBoxId === this.selectedBoxId); }
  get userId(): number | undefined { return this.session.getUser()?.id; }
  get cashAccounts(): ChartOfAccounts[] { return this.accounts.filter(a => a.accountType?.code?.toUpperCase() === 'CASH'); }
  get cashPurposes(): typeof this.purposes { return this.purposes.filter(p => p.value !== 'PAY_ADVANCE_OFFSET'); }
  get floatProposals(): WorkforceCashAdvance[] { return this.operationalAdvances.filter(a => ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(a.status || '')); }
  get tripPayRequests(): DeliveryCashRequest[] { return this.deliveryCashRequests.filter(r => r.purpose === 'ADVANCE_AGAINST_PAY'
    && (!r.pettycashboxid || r.pettycashboxid === this.selectedBoxId)); }
  get openPayAdvances(): { label: string; value: number }[] { return this.payAdvances.map(a => ({ label: `#${a.workerPayAdvanceId} · ${this.money(a.openAmount)} open`, value: a.workerPayAdvanceId })); }
  get tripPayOptions(): { label: string; value: number }[] { return this.tripPayRequests.map(r => ({ label: `Delivery #${r.deliveryid} · ${this.money(r.amount)} ${r.currencycode}`, value: r.deliverycashrequestid })); }
  get topUpSettlements(): WorkforceCashSettlement[] { return this.dueSettlements.filter(s => s.status === 'TOP_UP_DUE'); }
  get reimbursableSettlements(): WorkforceCashSettlement[] { return this.approvedSettlements.filter(s => !!s.outOfPocket); }
  get returnSourceOptions(): { label: string; value: string }[] {
    return [{ label: 'General return', value: '' },
      ...this.operationalAdvances.filter(a => a.status !== 'APPROVED').map(a => ({ label: `${a.advanceNumber} · ${this.money(a.openAmount)} open`, value: `ADVANCE_RETURN:${a.workforceCashAdvanceId}` })),
      ...this.dueSettlements.filter(s => s.status === 'RETURN_DUE').map(s => ({ label: `${s.settlementNumber} · ${this.money(s.cashReturnAmount)} due`, value: `WORKFORCE_SETTLEMENT_RETURN:${s.workforceCashSettlementId}` }))];
  }
  get dialogTitle(): string { return ({ BOX: 'New custodian cash box', ACTIVATE: 'Activate cash box', HANDOFF: 'Record cash handoff',
    APPROVE: 'Review cash handoff', REJECT: 'Reject cash handoff', WAGE_RETURN: 'Record returned wage cash',
    COUNT: 'Count physical cash', CLOSE: 'Close cash month', CANCEL_OFFSET: 'Cancel payroll offset',
    FUND_APPROVE: 'Approve funding request', TRANSFER: 'Transfer cash box custody', CANCEL_TRANSFER: 'Cancel custody transfer' } as Record<PettyDialogMode, string>)[this.dialogMode || 'BOX']; }
  get dialogDescription(): string { return ({ BOX: 'Connect one physical cash box to its company GL account and worker controls.',
    ACTIVATE: 'Confirm the cash in hand against the GL before this box can move money.',
    HANDOFF: 'Record each purpose in one physical handoff. Finance reviews and posts it.',
    APPROVE: 'Check the cash, source, and handoff evidence before posting.',
    REJECT: 'Keep a reason for the rejected draft without changing the cash book.',
    WAGE_RETURN: 'Document cash physically returned and send its linked reversal for finance approval.',
    COUNT: 'Record what you physically counted in the custodian box.',
    CLOSE: 'Close a completed month after a matching month-end count and GL check.',
    CANCEL_OFFSET: 'Cancel an unposted payroll deduction while leaving the advance outstanding.',
    FUND_APPROVE: 'Choose the company bank and transfer reference. Cash posts only after the custodian confirms full receipt.',
    TRANSFER: 'Finance authorizes the counted handover. The new custodian accepts before access changes.',
    CANCEL_TRANSFER: 'Record why the handover is stopping. The current custodian keeps access.' } as Record<PettyDialogMode, string>)[this.dialogMode || 'BOX']; }
  get dialogActionLabel(): string { return this.dialogMode === 'HANDOFF' ? `Send ${this.lines.length || (this.line.amount > 0 ? 1 : 0)} line(s) to finance`
    : this.dialogMode === 'APPROVE' ? this.dialogGroup?.[0]?.firstApprovedBy ? 'Second approval and post' : 'Approve and post'
    : ({ BOX: 'Create cash box', ACTIVATE: 'Reconcile and activate', REJECT: 'Reject handoff',
      WAGE_RETURN: 'Record returned cash', COUNT: 'Record count', CLOSE: 'Close month',
      CANCEL_OFFSET: 'Cancel offset', FUND_APPROVE: 'Approve funding', TRANSFER: 'Authorize transfer',
      CANCEL_TRANSFER: 'Cancel transfer' } as Partial<Record<PettyDialogMode, string>>)[this.dialogMode || 'BOX'] || 'Save'; }
  openDialog(mode: PettyDialogMode, group?: PettyCashDocument[], doc?: PettyCashDocument): void {
    this.dialogMode = mode; this.dialogGroup = group; this.dialogDocument = doc;
    this.error = ''; this.actionReason = ''; this.dialogVisible = true;
    if (mode === 'HANDOFF') { this.lines = []; this.line = { purpose: 'DIRECT_EXPENSE', amount: 0 }; this.selectedLiability = undefined; }
    if (mode === 'BOX') this.boxForm = { boxName: '', custodianId: undefined, locationId: undefined, currencyCode: 'SAR',
      cashAccountId: undefined, workerFloatAccountId: undefined, workerPayAdvanceAccountId: undefined };
    if (mode === 'FUND_APPROVE') { this.fundingBankId = undefined; this.fundingReference = ''; }
    if (mode === 'TRANSFER') { this.transferCustodianId = undefined; this.transferReason = ''; }
    if (mode === 'ACTIVATE') this.activationCount = undefined;
    if (mode === 'COUNT') { this.countAmount = undefined; this.countWitness = ''; }
    if (mode === 'WAGE_RETURN') { this.returnReason = ''; this.returnProof = ''; this.returnWitness = ''; }
    if (mode === 'APPROVE' || mode === 'REJECT') { this.approvalProof = ''; this.approvalWitness = ''; }
  }
  resetDialog(): void {
    this.error = '';
    if (this.dialogMode === 'BOX') this.boxForm = { boxName: '', custodianId: undefined, locationId: undefined, currencyCode: 'SAR',
      cashAccountId: undefined, workerFloatAccountId: undefined, workerPayAdvanceAccountId: undefined };
    if (this.dialogMode === 'HANDOFF') {
      this.lines = []; this.line = { purpose: 'DIRECT_EXPENSE', amount: 0 }; this.selectedLiability = undefined;
      this.submissionKey = ''; this.submissionFingerprint = '';
    }
  }
  saveDialog(): void {
    switch (this.dialogMode) {
      case 'BOX': this.createBox(); break;
      case 'FUND_APPROVE': this.approveFunding(); break;
      case 'TRANSFER': this.transferCustody(); break;
      case 'CANCEL_TRANSFER': this.cancelTransfer(); break;
      case 'ACTIVATE': this.activate(); break;
      case 'HANDOFF': this.submit(); break;
      case 'APPROVE': if (this.dialogGroup) this.approve(this.dialogGroup); break;
      case 'REJECT': if (this.dialogGroup) this.reject(this.dialogGroup); break;
      case 'WAGE_RETURN': if (this.dialogDocument) this.recordEarnedPayReturn(this.dialogDocument); break;
      case 'COUNT': this.count(); break;
      case 'CLOSE': this.close(); break;
      case 'CANCEL_OFFSET': if (this.dialogDocument) this.cancelPayrollOffset(this.dialogDocument); break;
    }
  }
  get activePayouts(): WorkforcePayoutStatement[] { return this.payouts.filter(p => p.status === 'RELEASED' && (!this.line.fieldWorkerId || p.fieldWorkerId === this.line.fieldWorkerId)); }
  get selectedSettlement(): WorkforceCashSettlement | undefined {
    return this.approvedSettlements.find(s => s.workforceCashSettlementId === this.line.sourceId);
  }
  get selectedPayout(): WorkforcePayoutStatement | undefined {
    return this.payouts.find(p => p.workforcePayoutStatementId === this.line.payoutStatementId);
  }
  get pendingGroups(): PettyCashDocument[][] {
    const grouped = new Map<string, PettyCashDocument[]>();
    this.pending.filter(doc => doc.pettyCashBoxId === this.selectedBoxId && doc.sourceKind !== 'CASH_BOX_TOP_UP_REQUEST')
      .forEach(doc => grouped.set(doc.groupKey, [...(grouped.get(doc.groupKey) || []), doc]));
    return [...grouped.values()];
  }
  get fundingRequests(): PettyCashDocument[] { return this.pending.filter(doc => doc.pettyCashBoxId === this.selectedBoxId
    && doc.sourceKind === 'CASH_BOX_TOP_UP_REQUEST'); }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && this.companyId) this.load();
    if (!this.boxesLoading || this.command?.name === 'BOX') this.handleCommand();
  }
  private handleCommand(): void {
    if (!this.command) return;
    const name = this.command.name;
    this.command = undefined;
    if (name === 'REFRESH') this.load();
    else if (name === 'BOX' || this.selectedBox) this.openDialog(name);
    else this.error = 'Choose a cash box for this action.';
    this.commandHandled.emit();
  }
  load(): void {
    if (!this.companyId) return;
    this.error = ''; this.accountsLoadError = '';
    this.boxesLoading = true;
    this.petty.boxes(this.companyId).subscribe({
      next: boxes => {
        this.boxesLoading = false;
        this.boxes = boxes;
        if (!this.boxes.some(box => box.pettyCashBoxId === this.selectedBoxId))
          this.selectedBoxId = this.boxes[0]?.pettyCashBoxId;
        this.selectBox();
        this.handleCommand();
      }, error: err => { this.boxesLoading = false;
        this.error = this.message(err, 'Cash boxes could not be loaded.'); this.command = undefined; this.commandHandled.emit(); }
    });
    this.chart.getAllChartOfAccountsByCompany(this.companyId, false).subscribe({
      next: rows => this.accounts = rows.filter(a => a.isActive && !a.deprecated),
      error: err => {
        this.accounts = [];
        this.accountsLoadError = this.message(err, 'GL accounts could not be loaded. Refresh this workspace.');
        this.error = this.accountsLoadError;
      }
    });
    this.petty.pending(this.companyId).subscribe({ next: rows => this.pending = rows,
      error: err => this.error = this.message(err, 'Pending cash records could not be loaded.') });
    if (this.canManage) this.petty.eligibleCustodians(this.companyId).subscribe({ next: rows => this.custodians = rows,
      error: err => this.error = this.message(err, 'Eligible custodians could not be loaded.') });
    this.petty.apOverlaps(this.companyId).subscribe({ next: rows => this.apOverlaps = rows,
      error: err => this.error = this.message(err, 'AP overlaps could not be loaded.') });
    this.petty.awaitingPayroll(this.companyId).subscribe({ next: rows => this.awaitingPayroll = rows,
      error: err => this.error = this.message(err, 'Payroll offsets could not be loaded.') });
    this.bankService.getBankAccounts('Company', this.companyId, '', 0, 100).subscribe({
      next: data => this.banks = data.items || [],
      error: err => this.error = this.message(err, 'Company bank accounts could not be loaded.') });
  }
  selectBox(): void {
    this.boxChanged.emit(this.selectedBox);
    this.activeTransfer = undefined;
    if (!this.companyId || !this.selectedBoxId) { this.book = []; this.counts = []; this.billOptions = []; this.activeTransfer = undefined; return; }
    const boxId = this.selectedBoxId;
    this.petty.activeCustodyTransfer(this.companyId, boxId).subscribe({ next: row => { if (this.selectedBoxId === boxId) this.activeTransfer = row || undefined; },
      error: err => { if (this.selectedBoxId === boxId) this.error = this.message(err, 'Custody transfer could not be loaded.'); } });
    this.petty.book(this.companyId, boxId).subscribe({ next: rows => { if (this.selectedBoxId === boxId) this.book = rows; },
      error: err => { if (this.selectedBoxId === boxId) this.error = this.message(err, 'Cash book could not be loaded.'); } });
    this.petty.counts(this.companyId, boxId).subscribe({ next: rows => { if (this.selectedBoxId === boxId) this.counts = rows; },
      error: err => { if (this.selectedBoxId === boxId) this.error = this.message(err, 'Cash counts could not be loaded.'); } });
    this.petty.billOptions(this.companyId, boxId).subscribe({ next: rows => { if (this.selectedBoxId === boxId) this.billOptions = rows; },
      error: err => { if (this.selectedBoxId === boxId) this.error = this.message(err, 'Posted bill options could not be loaded.'); } });
  }
  createBox(): void {
    if (!this.companyId) { this.error = 'Select a company before creating a cash box.'; return; }
    if (!this.canManage) { this.error = 'Your role needs Workforce Finance cash management access.'; return; }
    const form = this.boxForm;
    if (!form.boxName.trim() || !form.currencyCode.trim() || !form.custodianId || !form.cashAccountId
        || !form.workerFloatAccountId || !form.workerPayAdvanceAccountId) {
      this.error = 'Enter the box name, custodian, and three GL accounts.'; return;
    }
    if (new Set([form.cashAccountId, form.workerFloatAccountId, form.workerPayAdvanceAccountId]).size !== 3) {
      this.error = 'Petty cash, worker float, and pay advances need three different GL accounts.'; return;
    }
    if (!form.locationId) { this.error = 'Choose a saved company location for this cash box.'; return; }
    this.busy = true; this.error = '';
    this.petty.createBox({ ...form, boxName: form.boxName.trim(), currencyCode: form.currencyCode.trim().toUpperCase(),
      owningCompanyId: this.companyId }).subscribe({
      next: box => { this.selectedBoxId = box.pettyCashBoxId; this.dialogVisible = false;
        this.success = 'Cash box created. A finance colleague can now reconcile and activate it.'; this.load(); },
      error: err => { this.error = this.message(err, 'Cash box could not be created.'); this.busy = false; },
      complete: () => this.busy = false
    });
  }
  activate(): void {
    if (!this.companyId || !this.selectedBoxId) { this.error = 'Choose a cash box.'; return; }
    if (this.activationCount == null || this.activationCount < 0) { this.error = 'Enter the physical cash counted.'; return; }
    this.run(this.petty.activate(this.selectedBoxId, this.companyId, this.activationCount), 'Cash box activated.');
  }
  approveFunding(): void {
    if (!this.companyId || !this.dialogDocument || !this.fundingBankId || !this.fundingReference.trim()) {
      this.error = 'Choose the company bank and enter its transfer reference.'; return;
    }
    this.run(this.petty.approveFunding(this.companyId, this.dialogDocument.pettyCashDocumentId,
      this.fundingBankId, this.fundingReference.trim()), 'Funding approved. The custodian must confirm full physical receipt.');
  }
  transferCustody(): void {
    const count = this.counts[0];
    if (!this.companyId || !this.selectedBoxId || !this.transferCustodianId || !this.transferReason.trim()
        || !count || count.status !== 'MATCHED') {
      this.error = 'Choose a new custodian, record a matching physical count, and enter a reason.'; return;
    }
    this.run(this.petty.transferCustody(this.companyId, this.selectedBoxId, this.transferCustodianId,
      count.pettyCashCountId, this.transferReason.trim()), 'Transfer authorized. The new custodian must count and accept the cash.');
  }
  cancelTransfer(): void {
    if (!this.companyId || !this.activeTransfer || !this.actionReason.trim()) {
      this.error = 'Enter a reason for cancelling the custody transfer.'; return;
    }
    this.run(this.petty.cancelCustodyTransfer(this.companyId, this.activeTransfer.pettyCashCustodyTransferId,
      this.actionReason.trim()), 'Custody transfer cancelled. The current custodian retains access.');
  }
  workerChanged(): void {
    this.payAdvances = []; this.operationalAdvances = []; this.approvedSettlements = [];
    this.dueSettlements = []; this.workerDocuments = []; this.deliveryCashRequests = [];
    this.line.payoutStatementId = undefined; this.line.sourceId = undefined; this.selectedLiability = undefined;
    this.selectedReturnSource = '';
    if (this.companyId && this.line.fieldWorkerId) forkJoin({
      pay: this.petty.payAdvances(this.companyId, this.line.fieldWorkerId),
      documents: this.petty.workerDocuments(this.companyId, this.line.fieldWorkerId),
      deliveryCash: this.petty.deliveryCashRequests(this.companyId, this.line.fieldWorkerId),
      operational: this.workforce.advances({ owningCompanyId: this.companyId, fieldWorkerId: this.line.fieldWorkerId }),
      settlements: this.workforce.settlements({ owningCompanyId: this.companyId, fieldWorkerId: this.line.fieldWorkerId })
    }).subscribe({
      next: rows => {
        this.payAdvances = rows.pay.filter(a => a.openAmount > 0);
        this.workerDocuments = rows.documents;
        this.deliveryCashRequests = rows.deliveryCash;
        this.operationalAdvances = rows.operational.filter(a => ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'ISSUED', 'ACKNOWLEDGED'].includes(a.status || ''));
        this.approvedSettlements = rows.settlements.filter(s => s.status === 'APPROVED');
        this.dueSettlements = rows.settlements.filter(s => ['RETURN_DUE', 'TOP_UP_DUE'].includes(s.status || ''));
      }, error: err => this.error = this.message(err, 'Worker cash sources could not be loaded.')
    });
  }
  purposeChanged(): void {
    const purpose = this.line.purpose;
    this.line = { purpose, amount: 0, fieldWorkerId: this.line.fieldWorkerId };
    this.selectedLiability = undefined;
    this.selectedBill = undefined;
    this.selectedReturnSource = '';
    this.offsetKey = ''; this.offsetFingerprint = '';
  }
  settlementChanged(): void {
    const settlement = this.selectedSettlement;
    if (!settlement) return;
    this.line.amount = settlement.approvedAmount || 0;
    const accounts = [...new Set((settlement.lines || []).map(line => line.expenseAccountId).filter(Boolean))];
    this.line.expenseAccountId = accounts.length === 1 ? accounts[0] : undefined;
  }
  advanceChanged(): void {
    const advance = this.operationalAdvances.find(a => a.workforceCashAdvanceId === this.line.sourceId);
    if (!advance) { this.line.sourceKind = undefined; return; }
    if (advance) {
      this.line.sourceKind = this.line.purpose === 'FLOAT_ISSUE' ? 'WORKFORCE_CASH_ADVANCE' : 'ADVANCE_RETURN';
      this.line.amount = this.line.purpose === 'FLOAT_ISSUE'
        ? advance.approvedAmount || advance.requestedAmount || 0 : advance.openAmount || 0;
    }
  }
  deliveryCashChanged(): void {
    const request = this.deliveryCashRequests.find(row => row.deliverycashrequestid === this.line.sourceId);
    this.line.sourceKind = request ? 'DELIVERY_CASH_REQUEST' : undefined;
    if (request) this.line.amount = request.amount;
  }
  returnSourceChanged(): void {
    const [kind, id] = this.selectedReturnSource.split(':');
    this.line.sourceKind = kind || undefined;
    this.line.sourceId = id ? Number(id) : undefined;
    const advance = this.operationalAdvances.find(a => a.workforceCashAdvanceId === this.line.sourceId);
    const settlement = this.dueSettlements.find(s => s.workforceCashSettlementId === this.line.sourceId);
    this.line.amount = kind === 'ADVANCE_RETURN' ? advance?.openAmount || 0
      : kind === 'WORKFORCE_SETTLEMENT_RETURN' ? settlement?.cashReturnAmount || 0 : 0;
  }
  settlementDueChanged(): void {
    const settlement = this.dueSettlements.find(s => s.workforceCashSettlementId === this.line.sourceId);
    if (!settlement) { this.line.sourceKind = undefined; return; }
    this.line.sourceKind = this.line.purpose === 'FLOAT_RETURN'
      ? 'WORKFORCE_SETTLEMENT_RETURN' : 'WORKFORCE_SETTLEMENT_TOP_UP';
    this.line.amount = this.line.purpose === 'FLOAT_RETURN'
      ? settlement.cashReturnAmount || 0 : settlement.topUpAmount || 0;
  }
  searchBills(): void {
    if (!this.companyId || !this.selectedBoxId) return;
    this.petty.billOptions(this.companyId, this.selectedBoxId, this.billSearch).subscribe({
      next: rows => this.billOptions = rows,
      error: err => this.error = this.message(err, 'Posted bills could not be loaded.')
    });
  }
  billChanged(): void { if (this.selectedBill) this.line.amount = this.selectedBill.remaining; }
  payoutChanged(): void {
    this.liabilityOptions = []; this.selectedLiability = undefined;
    if (this.companyId && this.line.payoutStatementId) this.petty.liabilities(this.companyId, this.line.payoutStatementId).subscribe({
      next: rows => this.liabilityOptions = rows,
      error: err => this.error = this.message(err, 'Posted payout obligations could not be loaded.')
    });
  }
  addLine(): void {
    if (!(Number(this.line.amount) > 0)) { this.error = 'Enter a positive amount.'; return; }
    const entry: PettyCashLine = { ...this.line, amount: Number(this.line.amount) };
    if (entry.purpose === 'BILL_PAYMENT') {
      if (!this.selectedBill) { this.error = 'Choose a posted bill.'; return; }
      entry.liabilityType = 'AP_BILL'; entry.liabilityId = this.selectedBill.liabilityId;
      entry.liabilityAccountId = this.selectedBill.accountId;
      entry.sourceKind = 'AP_BILL'; entry.sourceId = this.selectedBill.liabilityId;
      entry.sourceReference = this.selectedBill.label;
    }
    if (entry.purpose === 'PAY_ADVANCE_OFFSET' && this.selectedPayout?.payoutTreatment === 'PAYROLL') {
      if (!this.selectedPayout.payrollInputId) { this.error = 'Released payroll payout has no payroll input.'; return; }
      entry.liabilityType = 'PAYROLL_INPUT'; entry.liabilityId = this.selectedPayout.payrollInputId;
    } else if (entry.purpose === 'EARNED_PAY' || entry.purpose === 'PAY_ADVANCE_OFFSET') {
      if (!this.selectedLiability) { this.error = 'Choose a posted payroll or AP obligation.'; return; }
      entry.liabilityType = this.selectedLiability.liabilityType;
      entry.liabilityId = this.selectedLiability.liabilityId;
      entry.liabilityAccountId = this.selectedLiability.accountId;
    }
    if (entry.purpose === 'FLOAT_ISSUE' && entry.sourceId) entry.sourceKind = 'WORKFORCE_CASH_ADVANCE';
    if (entry.purpose === 'FLOAT_RETURN' && entry.sourceId && !entry.sourceKind) entry.sourceKind = 'ADVANCE_RETURN';
    if (entry.purpose === 'REIMBURSE_EXPENSE') {
      if (!entry.sourceId) { this.error = 'Choose an approved worker expense settlement.'; return; }
      entry.sourceKind = 'WORKFORCE_SETTLEMENT';
    }
    if (entry.purpose === 'PAY_ADVANCE_OFFSET') {
      if (!entry.workerPayAdvanceId || !this.companyId || !this.selectedBoxId) {
        this.error = 'Choose the advance against pay to apply.'; return;
      }
      const fingerprint = JSON.stringify(entry);
      if (this.offsetFingerprint !== fingerprint) { this.offsetKey = crypto.randomUUID(); this.offsetFingerprint = fingerprint; }
      this.busy = true; this.error = '';
      this.petty.proposeOffset(this.companyId, this.selectedBoxId, entry, this.offsetKey).subscribe({
        next: () => {
          this.success = 'Advance offset sent for finance confirmation.'; this.offsetKey = ''; this.offsetFingerprint = '';
          this.dialogVisible = false; this.line = { purpose: 'DIRECT_EXPENSE', amount: 0, fieldWorkerId: entry.fieldWorkerId };
          this.selectedLiability = undefined; this.load();
        }, error: err => { this.error = this.message(err, 'Offset could not be proposed.'); this.busy = false; },
        complete: () => this.busy = false
      });
      return;
    }
    this.lines.push(entry); this.submissionKey = ''; this.submissionFingerprint = ''; this.error = '';
    this.line = { purpose: 'DIRECT_EXPENSE', amount: 0, fieldWorkerId: entry.fieldWorkerId };
    this.selectedLiability = undefined;
    this.selectedBill = undefined;
  }
  removeLine(index: number): void { this.lines.splice(index, 1); this.submissionKey = ''; this.submissionFingerprint = ''; }
  submit(): void {
    if (!this.companyId || !this.selectedBoxId) { this.error = 'Choose an active cash box.'; return; }
    if (!this.lines.length && this.line.amount > 0) {
      this.addLine();
      if (!this.lines.length) return;
    }
    if (!this.lines.length) { this.error = 'Add at least one cash purpose line.'; return; }
    const fingerprint = JSON.stringify({ box: this.selectedBoxId, lines: this.lines });
    if (this.submissionFingerprint !== fingerprint) {
      this.submissionKey = crypto.randomUUID(); this.submissionFingerprint = fingerprint;
    }
    this.busy = true; this.error = '';
    this.petty.handoff(this.companyId, this.selectedBoxId, this.lines, this.submissionKey).subscribe({
      next: () => { this.lines = []; this.submissionKey = ''; this.submissionFingerprint = ''; this.dialogVisible = false;
        this.success = 'Cash record sent to finance.'; this.load(); },
      error: err => { this.error = this.message(err, 'Cash record could not be saved.'); this.busy = false; },
      complete: () => this.busy = false
    });
  }
  approve(group: PettyCashDocument[]): void {
    if (!this.companyId) return;
    this.busy = true; this.error = '';
    const fileToUpload = this.financeProofFile;
    this.petty.approve(this.companyId, group[0].groupKey, this.approvalProof, this.approvalWitness).subscribe({
      next: rows => {
        if (fileToUpload && rows[0]?.pettyCashDocumentId) {
          this.petty.uploadFinanceProof(this.companyId!, rows[0].pettyCashDocumentId, fileToUpload).subscribe();
        }
        this.financeProofFile = undefined;
        this.dialogVisible = false; this.success = rows.every(row => row.status === 'POSTED')
        ? 'Cash posted to the GL.' : rows.some(row => row.status === 'AWAITING_PAYROLL')
          ? 'Deduction confirmed. It will post with the mapped payroll run.'
          : 'First approval recorded. A second finance user must post this cash.'; this.load(); },
      error: err => { this.error = this.message(err, 'Approval failed.'); this.busy = false; },
      complete: () => this.busy = false
    });
  }
  onFinanceProofSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files[0]) {
      this.financeProofFile = input.files[0];
    }
  }
  clearFinanceProofFile(input?: HTMLInputElement): void {
    this.financeProofFile = undefined;
    if (input) input.value = '';
  }
  downloadFinanceVoucher(doc: PettyCashDocument): void {
    if (!this.companyId || !doc.pettyCashDocumentId) return;
    this.petty.downloadFinanceVoucherPdf(this.companyId, doc.pettyCashDocumentId).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = `petty-cash-voucher-${doc.documentNumber || doc.pettyCashDocumentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      },
      error: err => {
        this.error = this.message(err, 'Could not generate voucher PDF from Reports API.');
      }
    });
  }
  reject(group: PettyCashDocument[]): void {
    if (!this.companyId) return;
    if (!this.actionReason.trim()) { this.error = 'Enter a rejection reason.'; return; }
    if (group.some(doc => doc.fundingReportedAmount != null) && !this.approvalProof.trim()) {
      this.error = 'Add proof that the cash was returned or the bank transfer was reversed.'; return;
    }
    this.run(this.petty.reject(this.companyId, group[0].groupKey,
      this.actionReason.trim(), this.approvalProof.trim()), 'Cash record rejected.');
  }
  cancelPayrollOffset(doc: PettyCashDocument): void {
    if (!this.companyId) return;
    if (!this.actionReason.trim()) { this.error = 'Enter a cancellation reason.'; return; }
    this.run(this.petty.cancelPayrollOffset(this.companyId, doc.pettyCashDocumentId, this.actionReason.trim()), 'Payroll offset cancelled.');
  }
  recordEarnedPayReturn(doc: PettyCashDocument): void {
    if (!this.companyId || !this.returnReason.trim() || !(this.returnWitness.trim() || this.returnProof.trim())) {
      this.error = 'Enter the correction reason and signed proof link or named witness.'; return;
    }
    this.run(this.petty.recordEarnedPayReturn(this.companyId, doc.pettyCashDocumentId,
      this.returnReason.trim(), this.returnProof.trim(), this.returnWitness.trim()),
      'Returned wage cash recorded for finance approval.');
  }
  count(): void {
    if (!this.companyId || !this.selectedBoxId) { this.error = 'Choose a cash box.'; return; }
    if (this.countAmount == null || this.countAmount < 0) { this.error = 'Enter the physical cash counted.'; return; }
    this.run(this.petty.count(this.companyId, this.selectedBoxId, this.countAmount, this.countWitness), 'Physical cash count recorded.');
  }
  close(): void {
    if (!this.companyId || !this.selectedBoxId) { this.error = 'Choose a cash box.'; return; }
    if (!this.closeMonthValue || !this.counts[0]) { this.error = 'Choose a month and record its month-end count first.'; return; }
    const [year, month] = this.closeMonthValue.split('-').map(Number);
    const end = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
    this.run(this.petty.close(this.companyId, this.selectedBoxId, end, this.counts[0].pettyCashCountId), 'Month closed.');
  }
  person(id?: number): string { return this.people.find(person => person.fieldWorkerId === id)?.displayName || (id ? `Worker #${id}` : ''); }
  account(id?: number): string { return this.accounts.find(a => a.accountId === id)?.accountName || (id ? `GL #${id}` : ''); }
  money(value?: number): string { return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  groupAmount(group: PettyCashDocument[]): number { return group.reduce((sum, doc) => sum + Number(doc.amount || 0), 0); }
  private run(request: import('rxjs').Observable<unknown>, success: string): void {
    this.busy = true; this.error = ''; this.success = '';
    request.subscribe({ next: () => { this.dialogVisible = false; this.success = success; this.load(); },
      error: err => { this.error = this.message(err, 'Petty cash action failed.'); this.busy = false; },
      complete: () => this.busy = false });
  }
  private message(err: any, fallback: string): string {
    if (err?.status === 403) return 'Your role is missing the permission for this petty cash action.';
    return err?.error?.message || err?.error?.detail || fallback;
  }
}
